import React, { useState, useEffect } from 'react';
import { Upload, Button, message as antdMessage, Alert, Progress, Typography } from 'antd';
import { UploadOutlined, ClockCircleOutlined } from '@ant-design/icons';
import type { UploadProps, UploadFile } from 'antd/es/upload/interface';
import { uploadMrp, MrpUploadApiResponse } from '../../services/api';

const { Text } = Typography;

interface MrpUploadProps {
  onMrpProcessed: (data: MrpUploadApiResponse) => void;
}

const MrpUpload: React.FC<MrpUploadProps> = ({ onMrpProcessed }) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState<string>('');
  const [estimatedTime, setEstimatedTime] = useState<number | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);

  // Timer for elapsed time
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (loading) {
      const startTime = Date.now();
      interval = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [loading]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const getFileSize = (file: UploadFile): number => {
    return file.size || 0;
  };

  const estimateProcessingTime = (fileSize: number): number => {
    // Rough estimation: 1MB = ~10 seconds, minimum 5 seconds
    const sizeInMB = fileSize / (1024 * 1024);
    return Math.max(5, Math.ceil(sizeInMB * 10));
  };

  const simulateProgress = (duration: number) => {
    const steps = [
      { step: 'Reading Excel file...', progress: 10, delay: 500 },
      { step: 'Validating columns (Material, Werk, times)...', progress: 25, delay: 800 },
      { step: 'Processing material data...', progress: 40, delay: 1200 },
      { step: 'Extracting unique Werks...', progress: 60, delay: 1000 },
      { step: 'Removing duplicates...', progress: 80, delay: 1500 },
      { step: 'Preparing data for Werk selection...', progress: 95, delay: 500 },
      { step: 'Finalizing...', progress: 100, delay: 300 }
    ];

    let currentStepIndex = 0;
    const totalSteps = steps.length;
    const stepDuration = duration / totalSteps;

    const processStep = () => {
      if (currentStepIndex < steps.length && loading) {
        const { step, progress } = steps[currentStepIndex];
        setCurrentStep(step);
        setProgress(progress);
        currentStepIndex++;
        
        if (currentStepIndex < steps.length) {
          setTimeout(processStep, stepDuration * 1000);
        }
      }
    };

    processStep();
  };

  const props: UploadProps = {
    onRemove: (file) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
      setError(null);
      setSuccessMessage(null);
      setEstimatedTime(null);
    },
    beforeUpload: (file) => {
      if (!file.name.match(/\.(xlsx|xls)$/)) {
        antdMessage.error(`${file.name} is not an Excel file (.xls or .xlsx)`);
        return Upload.LIST_IGNORE;
      }
      
      const fileSize = getFileSize(file);
      const estimated = estimateProcessingTime(fileSize);
      setEstimatedTime(estimated);
      
      setFileList([file]);
      setError(null);
      setSuccessMessage(null);
      console.log(`MRP file selected: ${file.name} (${(fileSize / 1024 / 1024).toFixed(2)} MB)`);
      console.log(`Estimated processing time: ${estimated} seconds`);
      
      return false; // Prevent auto upload
    },
    fileList,
    maxCount: 1,
  };

  const handleUpload = async () => {
    if (fileList.length === 0) {
      antdMessage.error('Please select a file first.');
      return;
    }

    const file = fileList[0];
    const fileSize = getFileSize(file);
    
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    setProgress(0);
    setCurrentStep('Preparing upload...');
    setElapsedTime(0);

    console.log('Starting MRP file upload and processing...');
    console.log(`File: ${file.name}, Size: ${(fileSize / 1024 / 1024).toFixed(2)} MB`);

    // Start progress simulation
    if (estimatedTime) {
      simulateProgress(estimatedTime);
    }

    try {
      const response = await uploadMrp(fileList[0] as any);
      
      setProgress(100);
      setCurrentStep('Complete!');
      onMrpProcessed(response.data);
      setSuccessMessage(response.data.message);
      antdMessage.success('MRP file processed successfully! Please select a Werk to continue.');
      
      console.log('MRP processing completed successfully');
      console.log(`Found ${response.data.available_werks.length} unique Werks`);
      console.log(`Available Werks: ${response.data.available_werks.slice(0, 5).join(', ')}${response.data.available_werks.length > 5 ? '...' : ''}`);
      
    } catch (err: any) {
      const apiError = err.response?.data?.detail || err.message || 'Failed to upload MRP file.';
      setError(apiError);
      antdMessage.error(apiError);
      setProgress(0);
      setCurrentStep('');
      
      console.error('MRP processing failed:', apiError);
    }
    
    setLoading(false);
  };

  return (
    <div>
      <Upload {...props}>
        <Button icon={<UploadOutlined />}>Select MRP File (.xls, .xlsx)</Button>
      </Upload>
      
      {fileList.length > 0 && estimatedTime && !loading && (
        <div style={{ marginTop: 8 }}>
          <Text type="secondary">
            <ClockCircleOutlined /> Estimated processing time: ~{formatTime(estimatedTime)}
          </Text>
        </div>
      )}
      
      <Button
        type="primary"
        onClick={handleUpload}
        disabled={fileList.length === 0}
        loading={loading}
        style={{ marginTop: 16 }}
      >
        {loading ? 'Processing...' : 'Process MRP File'}
      </Button>
      
      {loading && (
        <div style={{ marginTop: 16 }}>
          <Progress 
            percent={progress} 
            status={progress === 100 ? "success" : "active"}
            strokeColor={progress === 100 ? "#52c41a" : "#1890ff"}
          />
          <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between' }}>
            <Text type="secondary">{currentStep}</Text>
            <Text type="secondary">Elapsed: {formatTime(elapsedTime)}</Text>
          </div>
          {estimatedTime && elapsedTime < estimatedTime && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Estimated remaining: ~{formatTime(Math.max(0, estimatedTime - elapsedTime))}
            </Text>
          )}
        </div>
      )}
      
      {error && (
        <Alert 
          message={error} 
          type="error" 
          showIcon 
          closable 
          style={{ marginTop: 16 }} 
          onClose={() => setError(null)} 
        />
      )}
      
      {successMessage && !error && (
        <Alert 
          message={successMessage} 
          type="success" 
          showIcon 
          style={{ marginTop: 16 }} 
        />
      )}
    </div>
  );
};

export default MrpUpload;